import { useEffect, useRef, useState } from "react";
import type { CustomDomainSetup } from "../../../shared/generatedDrizzleSchema";
import { DetailList } from "../Settings/shared";
import { StatusPill } from "../StatusPill";
import { Text } from "../Text";
import { RecordHeading } from "./DomainDnsRecords";
import {
  attentionMessage,
  finishingSetup,
  settingUp,
  statusBadge,
} from "./status";

function progress(setup: CustomDomainSetup, finishing: boolean) {
  if (finishing)
    return {
      label: "Finishing setup",
      color: "gray" as const,
      running: true,
      detail:
        "We’re verifying the domain and issuing its HTTPS certificate. This usually takes a few minutes.",
    };
  if (settingUp(setup))
    return {
      label: "Pending",
      color: "gray" as const,
      detail:
        attentionMessage(setup) ??
        "We’ll issue the certificate once all DNS records are detected.",
    };
  return {
    ...statusBadge(setup),
    detail:
      attentionMessage(setup) ??
      (setup.status === "ready"
        ? "This domain is serving workflows over HTTPS."
        : "We’re removing this domain."),
  };
}

export function DomainSetupProgress({ setup }: { setup: CustomDomainSetup }) {
  const finishing = finishingSetup(setup);
  const { label, color, detail, running } = progress(setup, finishing);
  const ref = useRef<HTMLDivElement>(null);
  const [finishingOnOpen] = useState(finishing);
  useEffect(() => {
    if (finishing && !finishingOnOpen)
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [finishing, finishingOnOpen]);
  return (
    <DetailList ref={ref}>
      <RecordHeading title="Certificate" subtitle="Issued by 3B">
        <StatusPill
          passive
          running={running}
          color={color}
          label={label}
          detail={
            <Text variant="text-small-md" color="var(--tooltip-fg)">
              {detail}
            </Text>
          }
          compactIcon={
            color === "green"
              ? "check"
              : color === "red"
                ? "x"
                : "circle-dashed"
          }
        />
      </RecordHeading>
    </DetailList>
  );
}
